/**
 * The Complete Memberlist (CORD-02 §5): the Guestbook coalesced flat — latest
 * Join/Leave/Kick per npub wins — merged with the owner, minus the Banlist.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";

import { guestbookKey } from "@/lib/concord/crypto";
import { decodeStreamEvent, rumorTimeMs } from "@/lib/concord/stream";
import { memberRank, PERM, type ControlState } from "@/lib/concord/editions";
import { KIND_JOIN_LEAVE, KIND_KICK } from "@/lib/concord/config";
import { communityRelays } from "@/hooks/useChama";
import type { DecodedStreamEvent, Membership } from "@/lib/concord/types";

export interface MemberEntry {
  pubkey: string;
  joinedAtMs: number;
  isOwner: boolean;
  isStaff: boolean;
}

export function useMembers(
  membership: Membership | undefined,
  control: ControlState,
) {
  const { nostr } = useNostr();
  const communityId = membership?.communityId;
  const relays = communityRelays(membership);

  const query = useQuery({
    queryKey: ["chama", "guestbook", communityId],
    enabled: Boolean(membership),
    queryFn: async (c) => {
      const m = membership!.current;
      const gb = await guestbookKey(m.community_root, communityId!, m.root_epoch);
      const events = await nostr.group(relays).query(
        [{ kinds: [1059], authors: [gb.pk], limit: 500 }],
        { signal: c.signal },
      );
      return events
        .map((e) => decodeStreamEvent(e, gb))
        .filter((d): d is DecodedStreamEvent => d !== null);
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const members = useMemo(() => {
    if (!membership) return [] as MemberEntry[];
    const owner = membership.current.owner;
    const now = Date.now();

    // Coalesce: one final state per npub, latest wins, ties by lower rumor id.
    const state = new Map<string, { verb: string; atMs: number; id: string }>();
    const candidates = (query.data ?? [])
      .map((d) => {
        const atMs = rumorTimeMs(d.rumor);
        return atMs === null ? null : { d, atMs };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .filter(({ atMs }) => atMs <= now + 3_600_000) // >1h future is dropped
      .sort((a, b) => a.atMs - b.atMs || (a.d.rumor.id < b.d.rumor.id ? -1 : 1));

    for (const { d, atMs } of candidates) {
      const { rumor } = d;
      if (rumor.kind === KIND_JOIN_LEAVE) {
        const verb = rumor.content === "leave" ? "leave" : "join";
        state.set(rumor.pubkey, { verb, atMs, id: rumor.id });
      } else if (rumor.kind === KIND_KICK) {
        const target = rumor.tags.find(([n]) => n === "p")?.[1];
        if (!target) continue;
        // A Kick is honored only from a KICK holder who outranks the target.
        const kicker = memberRank(rumor.pubkey, owner, control.roles, control.grants);
        const targetRank = memberRank(target, owner, control.roles, control.grants);
        if ((kicker.permissions & PERM.KICK) === 0n) continue;
        if (kicker.position >= targetRank.position) continue;
        state.set(target, { verb: "kick", atMs, id: rumor.id });
      }
    }

    const out: MemberEntry[] = [];
    const seen = new Set<string>();
    for (const [pubkey, s] of state) {
      if (s.verb !== "join") continue;
      if (control.banlist.has(pubkey)) continue;
      seen.add(pubkey);
      const rank = memberRank(pubkey, owner, control.roles, control.grants);
      out.push({
        pubkey,
        joinedAtMs: s.atMs,
        isOwner: pubkey === owner,
        isStaff: rank.position !== Infinity && rank.position <= 1,
      });
    }
    if (!seen.has(owner)) {
      out.push({ pubkey: owner, joinedAtMs: 0, isOwner: true, isStaff: true });
    }
    out.sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || a.joinedAtMs - b.joinedAtMs);
    return out;
  }, [membership, query.data, control]);

  return { members, isLoading: query.isLoading };
}
